import React, {lazy} from 'react';
import {
  AuthLayout,
  DashboardLayout,
  DashboardLayoutV2,
  DefaultLayout,
  MainLayout,
  MemberProfileLayout,
  VideoLayout,
} from 'components';
import {PrivateRoute} from 'components/HOCs';
import {ExperiencesLayout} from 'components/Layouts/ExperiencesLayout';
import {ProviderProfileLayout} from 'components/Layouts/ProviderProfileLayout';
import {
  fullRoles,
  memberRoles,
  providerRoles,
  therapistRoles,
} from 'constants/roles';
import {LEGACY_REVENUE_ROUTES, ROUTES} from 'constants/routes';
import DPSetting from 'features/Provider/Revenue/DigitalPractice';
import GroupSession from 'features/Provider/Revenue/GroupSession';
import SingleSession from 'features/Provider/Revenue/SingleSession';
import {selectUserProfile} from 'features/User';
import WherebyVideoPage from 'features/Video/WherebyVideo/WherebyVideoPage';
import {ProviderFormWorkflow} from 'interfaces';
import {useSelector} from 'react-redux';
import {Redirect, Route} from 'react-router';
import {componentLoader, isProvider} from 'utils';
const BookingPage = lazy(() =>
  componentLoader(() => import('features/Appointment/Booking')),
);

const PopUpScreenPartiallyFilled = lazy(() =>
  componentLoader(
    () => import('features/Intake/IntakeSections/PopUpScreenPartiallyFilled'),
  ),
);
const RescheduleAppointmentPage = lazy(() =>
  componentLoader(() => import('features/Appointment/RescheduleAppointment')),
);
const OnboardingLobbyPage = lazy(() =>
  componentLoader(
    () => import('features/Appointment/Onboarding/OnboardingLobby'),
  ),
);
const OnboardingIntakePage = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/Appointment/Onboarding/PrescriberAppointments/MedicalIntake'
      ),
  ),
);
const CheckoutPage = lazy(() =>
  componentLoader(() => import('features/Payment/Checkout')),
);
const MembershipPage = lazy(() =>
  componentLoader(() => import('features/Payment/Membership')),
);
const CancelSubscriptionPage = lazy(() =>
  componentLoader(
    () => import('features/Payment/ManageSubscription/CancellationFlow'),
  ),
);
const ResubscriptionCheckoutPage = lazy(() =>
  componentLoader(() => import('features/Payment/ResubscriptionCheckout')),
);
const ResubscriptionPlansPage = lazy(() =>
  componentLoader(() => import('features/Payment/ResubscriptionPlans')),
);
const StripePaymentPage = lazy(() =>
  componentLoader(() => import('features/Payment/StripePayment')),
);
const OnboardingOneTimePaymentPage = lazy(() =>
  componentLoader(() => import('features/Payment/OnboardingOneTimePayment')),
);

const UpdatePlanPage = lazy(() =>
  componentLoader(() => import('features/Payment/UpdatePlan')),
);
const AddCreditCardPage = lazy(() =>
  componentLoader(() => import('features/Payment/AddCreditCard')),
);
const DashboardPage = lazy(() =>
  componentLoader(() => import('features/Member/Home')),
);
const ProfilePage = lazy(() =>
  componentLoader(() => import('features/Member/Profile/ProfilePage')),
);
const AccountPage = lazy(() =>
  componentLoader(() => import('features/Member/Profile')),
);

const TermsPage = lazy(() =>
  componentLoader(() => import('features/Member/TandC')),
);
const CreditCard = lazy(() =>
  componentLoader(() => import('features/Payment/CreditCardMgmt')),
);
const ReceiptsPage = lazy(() =>
  componentLoader(() => import('features/Payment/Receipts')),
);
const ThankYouPage = lazy(() =>
  componentLoader(() => import('features/Member/Thanks')),
);
const SingleNotificationPage = lazy(() =>
  componentLoader(() => import('features/Notification/Single')),
);

const NotificationsPage = lazy(() =>
  componentLoader(() => import('features/Notification/List')),
);

const SelfAwarenessPage = React.lazy(() =>
  componentLoader(() => import('features/Member/SelfAwarenessRequest')),
);
const ProgressPage = React.lazy(() =>
  componentLoader(() => import('features/Member/Progress')),
);
const UpdateProgressFormPage = React.lazy(() =>
  componentLoader(
    () => import('features/Member/Progress/UpdateProgressForm/main'),
  ),
);

const MemberDetailsPage = lazy(() =>
  componentLoader(() => import('features/Provider/Member')),
);

const MembersPage = lazy(() =>
  componentLoader(() => import('features/Provider/Members')),
);

const ProviderDashboardPage = lazy(() =>
  componentLoader(() => import('features/Provider/Dashboard')),
);

const ProviderProfilePage = lazy(() =>
  componentLoader(() => import('features/Provider/Profile')),
);
const ProviderProfileDetailsPage = lazy(() =>
  componentLoader(() => import('features/Provider/Profile/Details')),
);
const ProviderProfileInNetworkPage = lazy(() =>
  componentLoader(() => import('features/Provider/Profile/InNetwork')),
);
const ProviderProfileSettingsPage = lazy(() =>
  componentLoader(() => import('features/Provider/Profile/Settings')),
);

const ProviderProfileFaqsPage = lazy(() =>
  componentLoader(() => import('features/Provider/Profile/FAQs')),
);

const ProviderCalendarPage = lazy(() =>
  componentLoader(() => import('features/Provider/Calendar2')),
);

const ProviderAvailabilityPage = lazy(() =>
  componentLoader(() => import('features/Provider/Availability')),
);

const MessagingPage = lazy(() =>
  componentLoader(() => import('features/Messaging/main')),
);
const ProviderMessagingPage = lazy(() =>
  componentLoader(() => import('features/Messaging/ProviderMessagingPage')),
);

const DigitalProviderPage = lazy(() =>
  componentLoader(() => import('features/Provider/DigitalPractice')),
);

const DigitalPracticeMarketingPage = lazy(() =>
  componentLoader(
    () => import('features/Provider/DigitalPractice/DigitalPracticeMarketing'),
  ),
);

const EarnWellbitsPage = lazy(() =>
  componentLoader(() => import('features/Provider/EarnWellbits/MainContent')),
);

const WellniteContentPage = lazy(() =>
  componentLoader(() => import('features/Provider/Marketing/WellniteContent')),
);

const ProviderExperiencesPage = lazy(() =>
  componentLoader(() => import('features/Provider/Experiences')),
);

const ExperiencesPage = lazy(() =>
  componentLoader(() => import('features/Member/Experiences')),
);

const WellniteArtGalleryPage = lazy(() =>
  componentLoader(
    () => import('features/Member/Experiences/WellniteArt/Gallery'),
  ),
);

const SuperCoach = lazy(() =>
  componentLoader(
    () => import('features/Member/Experiences/SuperCoach/screen'),
  ),
);

const WellniteAudiosLaunch = lazy(() =>
  componentLoader(
    () => import('features/Provider/Experiences/WellniteAudios/AudioLaunch'),
  ),
);

const WellniteAudiosClonePage = lazy(() =>
  componentLoader(
    () => import('features/Provider/Experiences/WellniteAudios/VoiceCloning'),
  ),
);

const WellniteAudiosPage = lazy(() =>
  componentLoader(() => import('features/Provider/Experiences/WellniteAudios')),
);

const AudiosLibraryPage = lazy(() =>
  componentLoader(
    () => import('features/Provider/Experiences/WellniteAudios/Library'),
  ),
);

const WellniteAudiosTerms = lazy(() =>
  componentLoader(
    () => import('features/Provider/Experiences/WellniteAudios/TermsOfUse'),
  ),
);

const WellniteArtTerms = lazy(() =>
  componentLoader(
    () => import('features/Member/Experiences/WellniteArt/TermsOfUse'),
  ),
);

const RedirectAuth = lazy(() =>
  componentLoader(
    () => import('features/Provider/DigitalPractice/Revenue/RedirectAuth'),
  ),
);
const StripeOnboarded = lazy(() =>
  componentLoader(
    () => import('features/Provider/DigitalPractice/Revenue/StripeOnboarded'),
  ),
);
const SyncPayPalSellerInfoPage = lazy(() =>
  componentLoader(
    () =>
      import('features/Provider/DigitalPractice/Revenue/SyncPayPalSellerInfo'),
  ),
);
const VerifyPaystackTransaction = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/Payment/DigitalPracticeCheckout/Paystack/VerifyPaystackCard'
      ),
  ),
);
const DigitalPracticeOptionsPage = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/User/Register/OutsideWellniteRegister/DigitalPracticeOptions'
      ),
  ),
);
const ProviderProfileFormPage = lazy(() =>
  componentLoader(
    () => import('features/Provider/Register/Onboarding/ProfileForm'),
  ),
);

const ProviderCaqhFormPage = lazy(() =>
  componentLoader(
    () => import('features/Provider/Register/Onboarding/CaqhForm'),
  ),
);

const ProviderOnboardingFormPage = lazy(() =>
  componentLoader(
    () => import('features/Provider/Register/Onboarding/OnboardingForm'),
  ),
);

const ProviderDashboardOnboardingPage = lazy(() =>
  componentLoader(() => import('features/Provider/Register/Onboarding')),
);

const ProviderVerificationForm = lazy(() =>
  componentLoader(
    () => import('features/Provider/Register/Onboarding/Verification'),
  ),
);

const MemberIntakePage = lazy(() =>
  componentLoader(() => import('features/Intake')),
);

const InternationalProviderOnboarding = lazy(() =>
  componentLoader(
    () => import('features/Provider/Register/InternationalProviderOnboarding'),
  ),
);
const InternationalProviderTerms = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/Provider/Register/InternationalProviderOnboarding/TermsOfServiceForm'
      ),
  ),
);
const InternationalProviderProofOfId = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/Provider/Register/InternationalProviderOnboarding/ProofOfIdForm'
      ),
  ),
);

const ShortsGeneratePage = lazy(() =>
  componentLoader(
    () =>
      import(
        'features/Provider/Experiences/WellniteAudios/Shorts/GenerateShortPage'
      ),
  ),
);

export const PrivateRoutes = (): JSX.Element[] => {
  const user = useSelector(selectUserProfile);

  return [
    <Redirect
      exact
      from={ROUTES.PROVIDER.REVENUE.ROOT}
      to={ROUTES.PROVIDER.REVENUE.SINGLE_SESSIONS}
    />,
    <Redirect
      exact
      from={LEGACY_REVENUE_ROUTES.PROFILE_REVENUE}
      to={ROUTES.PROVIDER.REVENUE.DIGITAL_PRACTICE}
    />,
    <PrivateRoute
      exact
      path={ROUTES.RESUBSCRIBE_PLAN}
      key={ROUTES.RESUBSCRIBE_PLAN}
      component={ResubscriptionPlansPage}
      layoutProps={{lgColumns: 'grid-cols-1'}}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.RESUBSCRIBE_CHECKOUT}
      key={ROUTES.RESUBSCRIBE_CHECKOUT}
      component={ResubscriptionCheckoutPage}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PAYMENT.TOGETHER_ONE_TIME_PAYMENT}
      key={ROUTES.PAYMENT.TOGETHER_ONE_TIME_PAYMENT}
      component={StripePaymentPage}
      layout={DashboardLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.REGISTER_STRIPE}
      key={ROUTES.REGISTER_STRIPE}
      component={CheckoutPage}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.THANKS}
      key={ROUTES.THANKS}
      authorizedRoles={memberRoles}
      component={ThankYouPage}
    />,
    <PrivateRoute
      exact
      path={ROUTES.DASHBOARD}
      key={ROUTES.DASHBOARD}
      component={isProvider(user) ? ProviderDashboardPage : DashboardPage}
      layout={DashboardLayoutV2}
      authorizedRoles={fullRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.MEMBER.DETAILS}
      key={ROUTES.MEMBER.DETAILS}
      component={MemberDetailsPage}
      layout={DashboardLayoutV2}
      authorizedRoles={providerRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.VIDEO_CALL}
      key={ROUTES.VIDEO_CALL}
      component={WherebyVideoPage}
      layout={VideoLayout}
      authorizedRoles={fullRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.APPOINTMENT.BOOK}
      key={ROUTES.APPOINTMENT.BOOK}
      component={BookingPage}
      layout={DashboardLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.APPOINTMENT.RESCHEDULE}
      key={ROUTES.APPOINTMENT.RESCHEDULE}
      component={RescheduleAppointmentPage}
      layout={DashboardLayout}
      authorizedRoles={memberRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.MEMBER.PROGRESS}
      key={ROUTES.MEMBER.PROGRESS}
      component={ProgressPage}
      layout={DashboardLayoutV2}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.COACH}
      key={ROUTES.EXPERIENCES.COACH}
      component={SuperCoach}
      layout={MainLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.AUDIOS.LAUNCH}
      key={ROUTES.EXPERIENCES.AUDIOS.LAUNCH}
      component={WellniteAudiosLaunch}
      layout={ExperiencesLayout}
      layoutProps={{bgColor: 'bg-pink-100'}}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.AUDIOS.ROOT}
      key={ROUTES.EXPERIENCES.AUDIOS.ROOT}
      component={WellniteAudiosPage}
      layout={ExperiencesLayout}
      layoutProps={{bgColor: 'bg-pink-100'}}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.AUDIOS.LIBRARY}
      key={ROUTES.EXPERIENCES.AUDIOS.LIBRARY}
      component={AudiosLibraryPage}
      layoutProps={{bgColor: 'bg-pink-100'}}
      layout={ExperiencesLayout}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.AUDIOS.CLONE}
      key={ROUTES.EXPERIENCES.AUDIOS.CLONE}
      component={WellniteAudiosClonePage}
      layoutProps={{bgColor: 'bg-pink-100'}}
      layout={ExperiencesLayout}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.AUDIOS.GENERATE_SHORTS}
      key={ROUTES.EXPERIENCES.AUDIOS.GENERATE_SHORTS}
      component={ShortsGeneratePage}
      layoutProps={{bgColor: 'bg-pink-100'}}
      layout={ExperiencesLayout}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.ART.GALLERY}
      key={ROUTES.EXPERIENCES.ART.GALLERY}
      component={WellniteArtGalleryPage}
      layoutProps={{bgColor: 'bg-white'}}
      layout={ExperiencesLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.ART.TERMS}
      key={ROUTES.EXPERIENCES.ART.TERMS}
      component={WellniteArtTerms}
      layoutProps={{lgColumns: 'grid-cols-1'}}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.AUDIOS.TERMS}
      key={ROUTES.EXPERIENCES.AUDIOS.TERMS}
      component={WellniteAudiosTerms}
      layoutProps={{lgColumns: 'grid-cols-1'}}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.ROOT}
      key={ROUTES.EXPERIENCES.ROOT}
      component={isProvider(user) ? ProviderExperiencesPage : ExperiencesPage}
      layout={ExperiencesLayout}
      authorizedRoles={fullRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.PROGRESS_UPDATE}
      key={ROUTES.MEMBER.PROGRESS_UPDATE}
      component={UpdateProgressFormPage}
      layout={DashboardLayoutV2}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.SELF_AWARENESS}
      key={ROUTES.MEMBER.SELF_AWARENESS}
      component={SelfAwarenessPage}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.ONBOARDING_LOBBY}
      key={ROUTES.MEMBER.ONBOARDING_LOBBY}
      component={OnboardingLobbyPage}
      layout={MainLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.ONBOARDING_MEDICAL_INTAKE}
      key={ROUTES.MEMBER.ONBOARDING_MEDICAL_INTAKE}
      component={OnboardingIntakePage}
      authorizedRoles={memberRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.PAYMENT.UPDATE_PLAN}
      key={ROUTES.PAYMENT.UPDATE_PLAN}
      component={UpdatePlanPage}
      layout={DashboardLayoutV2}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.ACCOUNT}
      key={ROUTES.MEMBER.ACCOUNT}
      component={AccountPage}
      layout={DashboardLayoutV2}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.PROFILE}
      key={ROUTES.MEMBER.PROFILE}
      component={ProfilePage}
      layout={MemberProfileLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.NOTIFICATIONS.LIST}
      key={ROUTES.NOTIFICATIONS.LIST}
      component={NotificationsPage}
      layout={DashboardLayoutV2}
      authorizedRoles={fullRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.NOTIFICATIONS.SINGLE}
      key={ROUTES.NOTIFICATIONS.SINGLE}
      component={SingleNotificationPage}
      layout={DashboardLayout}
      authorizedRoles={memberRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.MEMBER.MEMBERSHIP}
      key={ROUTES.MEMBER.MEMBERSHIP}
      component={MembershipPage}
      layout={MemberProfileLayout}
      authorizedRoles={memberRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.TERMS}
      key={ROUTES.TERMS}
      component={TermsPage}
      layout={MemberProfileLayout}
      authorizedRoles={memberRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.MEMBER.PAYMENT}
      key={ROUTES.MEMBER.PAYMENT}
      component={CreditCard}
      layout={MemberProfileLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.RECEIPTS}
      key={ROUTES.MEMBER.RECEIPTS}
      component={ReceiptsPage}
      layout={MemberProfileLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PAYMENT.CANCEL_SUBSCRIPTION}
      key={ROUTES.PAYMENT.CANCEL_SUBSCRIPTION}
      component={CancelSubscriptionPage}
      layout={DashboardLayout}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PAYMENT.ADD_CREDIT_CARD}
      key={ROUTES.PAYMENT.ADD_CREDIT_CARD}
      component={AddCreditCardPage}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PAYMENT.ONBOARDING_ONE_TIME_PAYMENT}
      key={ROUTES.PAYMENT.ONBOARDING_ONE_TIME_PAYMENT}
      component={OnboardingOneTimePaymentPage}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.ROOT}
      key={ROUTES.MEMBER.ROOT}
      component={MemberDetailsPage}
      authorizedRoles={fullRoles}
      layout={DashboardLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.MEMBERS}
      key={ROUTES.PROVIDER.MEMBERS}
      component={MembersPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.PROFILE}
      key={ROUTES.PROVIDER.PROFILE}
      component={ProviderProfilePage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.PROFILE_DETAILS}
      key={ROUTES.PROVIDER.PROFILE_DETAILS}
      component={ProviderProfileDetailsPage}
      authorizedRoles={providerRoles}
      layout={ProviderProfileLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.PROFILE_IN_NETWORK}
      key={ROUTES.PROVIDER.PROFILE_IN_NETWORK}
      component={ProviderProfileInNetworkPage}
      authorizedRoles={providerRoles}
      layout={ProviderProfileLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.PROFILE_SETTINGS}
      key={ROUTES.PROVIDER.PROFILE_SETTINGS}
      component={ProviderProfileSettingsPage}
      authorizedRoles={providerRoles}
      layout={ProviderProfileLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.PROFILE_RESOURCE_CENTER}
      key={ROUTES.PROVIDER.PROFILE_RESOURCE_CENTER}
      component={ProviderProfileFaqsPage}
      authorizedRoles={providerRoles}
      layout={ProviderProfileLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.CALENDAR}
      key={ROUTES.PROVIDER.CALENDAR}
      component={ProviderCalendarPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.CALENDAR_MODE}
      key={ROUTES.PROVIDER.CALENDAR_MODE}
      component={ProviderCalendarPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,

    <PrivateRoute
      exact
      path={ROUTES.CALENDAR}
      key={ROUTES.CALENDAR}
      component={ProviderCalendarPage}
      authorizedRoles={memberRoles}
      layout={DashboardLayoutV2}
    />,

    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.AVAILABILITY}
      key={ROUTES.PROVIDER.AVAILABILITY}
      component={ProviderAvailabilityPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,

    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.MESSAGE}
      key={ROUTES.PROVIDER.MESSAGE}
      component={ProviderMessagingPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,

    <PrivateRoute
      exact
      path={ROUTES.MESSAGE}
      key={ROUTES.MESSAGE}
      component={MessagingPage}
      authorizedRoles={memberRoles}
      layout={DashboardLayoutV2}
    />,

    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.DIGITAL_PRACTICE.ROOT}
      key={ROUTES.PROVIDER.DIGITAL_PRACTICE.ROOT}
      component={DigitalProviderPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayout}
    />,

    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.DIGITAL_PRACTICE.MARKETING}
      key={ROUTES.PROVIDER.DIGITAL_PRACTICE.MARKETING}
      component={DigitalPracticeMarketingPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayout}
    />,

    <PrivateRoute
      exact
      path={ROUTES.STRIPE.REAUTH}
      key={ROUTES.STRIPE.REAUTH}
      component={RedirectAuth}
      authorizedRoles={therapistRoles}
      layout={DefaultLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.STRIPE.ONBOARDED}
      key={ROUTES.STRIPE.ONBOARDED}
      component={StripeOnboarded}
      authorizedRoles={therapistRoles}
      layout={DefaultLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.PAYPAL.SYNC_SELLER_INFO}
      key={ROUTES.PROVIDER.PAYPAL.SYNC_SELLER_INFO}
      component={SyncPayPalSellerInfoPage}
      authorizedRoles={therapistRoles}
      layout={DefaultLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PAYMENT.PAYSTACK_VERIFY_CARD}
      key={ROUTES.PAYMENT.PAYSTACK_VERIFY_CARD}
      component={VerifyPaystackTransaction}
      authorizedRoles={memberRoles}
      layout={DefaultLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.DIGITAL_PRACTICE.OPTIONS}
      key={ROUTES.DIGITAL_PRACTICE.OPTIONS}
      component={DigitalPracticeOptionsPage}
      authorizedRoles={memberRoles}
    />,

    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.DIGITAL_PRACTICE.EARNINGS}
      key={ROUTES.PROVIDER.DIGITAL_PRACTICE.EARNINGS}
      component={EarnWellbitsPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,
    <Route
      exact
      path={ROUTES.PROVIDER.MARKETING}
      key={ROUTES.PROVIDER.MARKETING}
    >
      <Redirect to={ROUTES.EXPERIENCES.CONTENT} />
    </Route>,
    <PrivateRoute
      exact
      path={ROUTES.EXPERIENCES.CONTENT}
      key={ROUTES.EXPERIENCES.CONTENT}
      component={WellniteContentPage}
      authorizedRoles={providerRoles}
      layout={DashboardLayoutV2}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION_VERIFICATION}
      key={ROUTES.PROVIDER.REGISTRATION_VERIFICATION}
      component={ProviderVerificationForm}
      authorizedRoles={providerRoles}
      layout={AuthLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION_CAQH}
      key={ROUTES.PROVIDER.REGISTRATION_CAQH}
      component={() => (
        <ProviderCaqhFormPage workflow={ProviderFormWorkflow.onboarding} />
      )}
      authorizedRoles={providerRoles}
      layout={AuthLayout}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION_ONBOARDING}
      key={ROUTES.PROVIDER.REGISTRATION_ONBOARDING}
      component={ProviderDashboardOnboardingPage}
      authorizedRoles={providerRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION_PROFILE}
      key={ROUTES.PROVIDER.REGISTRATION_PROFILE}
      component={ProviderProfileFormPage}
      authorizedRoles={providerRoles}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
      }}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.REGISTRATION_ONBOARDING_FORM}
      key={ROUTES.PROVIDER.REGISTRATION_ONBOARDING_FORM}
      component={ProviderOnboardingFormPage}
      authorizedRoles={providerRoles}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
      }}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.ONBOARDING_THERAPY_INTAKE}
      key={ROUTES.MEMBER.ONBOARDING_THERAPY_INTAKE}
      component={MemberIntakePage}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.MEMBER.INTAKE_POPUP}
      key={ROUTES.MEMBER.INTAKE_POPUP}
      component={PopUpScreenPartiallyFilled}
      authorizedRoles={memberRoles}
    />,
    <PrivateRoute
      exact
      path={ROUTES.PROVIDER.INTERNATIONAL_REGISTRATION}
      key={ROUTES.PROVIDER.INTERNATIONAL_REGISTRATION}
      component={InternationalProviderOnboarding}
      authorizedRoles={providerRoles}
      layout={AuthLayout}
      layoutProps={{
        bgColor: 'bg-white',
      }}
    />,
    <PrivateRoute
      exact
      authorizedRoles={providerRoles}
      path={ROUTES.PROVIDER.INTERNATIONAL_REGISTRATION_PROOF_OF_ID}
      key={ROUTES.PROVIDER.INTERNATIONAL_REGISTRATION_PROOF_OF_ID}
      component={InternationalProviderProofOfId}
      layout={AuthLayout}
    />,
    <PrivateRoute
      exact
      authorizedRoles={providerRoles}
      path={ROUTES.PROVIDER.INTERNATIONAL_REGISTRATION_TERMS}
      key={ROUTES.PROVIDER.INTERNATIONAL_REGISTRATION_TERMS}
      component={InternationalProviderTerms}
      layout={MainLayout}
    />,
    <PrivateRoute
      exact
      authorizedRoles={providerRoles}
      path={ROUTES.PROVIDER.REVENUE.SINGLE_SESSIONS}
      key={ROUTES.PROVIDER.REVENUE.SINGLE_SESSIONS}
      component={SingleSession}
      layout={DashboardLayoutV2}
    />,
    <PrivateRoute
      exact
      authorizedRoles={providerRoles}
      path={ROUTES.PROVIDER.REVENUE.GROUP_SESSIONS}
      key={ROUTES.PROVIDER.REVENUE.GROUP_SESSIONS}
      component={GroupSession}
      layout={DashboardLayoutV2}
    />,
    <PrivateRoute
      exact
      authorizedRoles={providerRoles}
      path={ROUTES.PROVIDER.REVENUE.DIGITAL_PRACTICE}
      key={ROUTES.PROVIDER.REVENUE.DIGITAL_PRACTICE}
      component={DPSetting}
      layout={DashboardLayoutV2}
    />,
  ];
};
